import { useEffect, useState } from 'react';
import * as api from '../services/api';
import type { CharacterPillar, HomeActivity, LearningLecture, Mission } from '../types/characterPillar';
import type { ClassContentSelection } from '../types/classActivityAssignment';

type PillarChildren = {
  missions: Mission[];
  lectures: LearningLecture[];
  homeActivities: HomeActivity[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
};

interface ClassContentPickerProps {
  value: ClassContentSelection;
  onChange: (next: ClassContentSelection) => void;
  disabled?: boolean;
}

async function fetchPillarChildren(pillarId: string): Promise<{
  missions: Mission[];
  lectures: LearningLecture[];
  homeActivities: HomeActivity[];
}> {
  const [missions, lectures, homeActivities] = await Promise.all([
    api.listMissions(pillarId, { activeOnly: true }),
    api.listLearningLectures(pillarId, { activeOnly: true }),
    api.listHomeActivities(pillarId, { activeOnly: true })
  ]);
  return { missions, lectures, homeActivities };
}

function stripPillarChildren(
  value: ClassContentSelection,
  children: { missions: Mission[]; lectures: LearningLecture[]; homeActivities: HomeActivity[] }
): Pick<ClassContentSelection, 'missionIds' | 'learningLectureIds' | 'homeActivityIds'> {
  const missionIdsToRemove = new Set(children.missions.map((m) => m.id));
  const lectureIdsToRemove = new Set(children.lectures.map((l) => l.id));
  const homeIdsToRemove = new Set(children.homeActivities.map((h) => h.id));

  return {
    missionIds: value.missionIds.filter((id) => !missionIdsToRemove.has(id)),
    learningLectureIds: value.learningLectureIds.filter((id) => !lectureIdsToRemove.has(id)),
    homeActivityIds: value.homeActivityIds.filter((id) => !homeIdsToRemove.has(id))
  };
}

export function ClassContentPicker({ value, onChange, disabled = false }: ClassContentPickerProps) {
  const [pillars, setPillars] = useState<CharacterPillar[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [childrenByPillar, setChildrenByPillar] = useState<Record<string, PillarChildren>>({});

  useEffect(() => {
    let cancelled = false;

    async function loadPillars() {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const items = await api.listCharacterPillars({ activeOnly: true });
        if (!cancelled) setPillars(items);
      } catch (err) {
        if (!cancelled) {
          setCatalogError(err instanceof Error ? err.message : 'Failed to load character pillars');
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    void loadPillars();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const selected = value.characterPillarIds;
    if (selected.length === 0) return;

    let cancelled = false;

    async function loadChildren(pillarId: string) {
      setChildrenByPillar((current) => ({
        ...current,
        [pillarId]: {
          missions: current[pillarId]?.missions ?? [],
          lectures: current[pillarId]?.lectures ?? [],
          homeActivities: current[pillarId]?.homeActivities ?? [],
          loading: true,
          loaded: current[pillarId]?.loaded ?? false,
          error: null
        }
      }));

      try {
        const data = await fetchPillarChildren(pillarId);
        if (cancelled) return;
        setChildrenByPillar((current) => ({
          ...current,
          [pillarId]: {
            ...data,
            loading: false,
            loaded: true,
            error: null
          }
        }));
      } catch (err) {
        if (cancelled) return;
        setChildrenByPillar((current) => ({
          ...current,
          [pillarId]: {
            missions: [],
            lectures: [],
            homeActivities: [],
            loading: false,
            loaded: false,
            error: err instanceof Error ? err.message : 'Failed to load pillar content'
          }
        }));
      }
    }

    for (const pillarId of selected) {
      const existing = childrenByPillar[pillarId];
      if (existing?.loaded || existing?.loading) continue;
      void loadChildren(pillarId);
    }

    return () => {
      cancelled = true;
    };
  }, [value.characterPillarIds.join(',')]);

  async function togglePillar(pillarId: string) {
    const selected = value.characterPillarIds.includes(pillarId);
    if (!selected) {
      onChange({
        ...value,
        characterPillarIds: [...value.characterPillarIds, pillarId]
      });
      return;
    }

    let children = childrenByPillar[pillarId];
    if (!children?.loaded) {
      try {
        const data = await fetchPillarChildren(pillarId);
        children = { ...data, loading: false, loaded: true, error: null };
        setChildrenByPillar((current) => ({ ...current, [pillarId]: children! }));
      } catch {
        children = {
          missions: [],
          lectures: [],
          homeActivities: [],
          loading: false,
          loaded: false,
          error: null
        };
      }
    }

    const stripped = stripPillarChildren(value, children);
    onChange({
      ...stripped,
      characterPillarIds: value.characterPillarIds.filter((id) => id !== pillarId)
    });
  }

  function toggleId(
    field: 'missionIds' | 'learningLectureIds' | 'homeActivityIds',
    id: string
  ) {
    const current = value[field];
    onChange({
      ...value,
      [field]: current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    });
  }

  if (catalogLoading) {
    return <p className="muted">Loading character pillars…</p>;
  }

  if (catalogError) {
    return (
      <p className="form-error" role="alert">
        {catalogError}
      </p>
    );
  }

  if (pillars.length === 0) {
    return (
      <p className="muted">
        No active character pillars yet. A platform admin must create pillars before you can assign
        content.
      </p>
    );
  }

  return (
    <div className="class-content-picker">
      <p className="muted class-content-picker-hint">
        Select pillars first, then choose missions, learning lectures, and home activities under
        each. Assigned XP sums become that class&apos;s pillar target.
      </p>
      {pillars.map((pillar) => {
        const pillarSelected = value.characterPillarIds.includes(pillar.id);
        const children = childrenByPillar[pillar.id];
        const selectedTargetXp =
          children?.loaded && !children.error
            ? children.missions
                .filter((m) => value.missionIds.includes(m.id))
                .reduce((sum, m) => sum + (m.xp ?? 0), 0) +
              children.lectures
                .filter((l) => value.learningLectureIds.includes(l.id))
                .reduce((sum, l) => sum + (l.xp ?? 0), 0) +
              children.homeActivities
                .filter((h) => value.homeActivityIds.includes(h.id))
                .reduce((sum, h) => sum + (h.xp ?? 0), 0)
            : null;

        return (
          <div key={pillar.id} className="class-content-pillar">
            <label className="checkbox-row class-content-pillar-toggle">
              <input
                type="checkbox"
                checked={pillarSelected}
                disabled={disabled}
                onChange={() => void togglePillar(pillar.id)}
              />
              <span>
                <strong>{pillar.name}</strong>
                {pillar.description ? (
                  <span className="cell-sub">{pillar.description}</span>
                ) : null}
                {pillarSelected && selectedTargetXp !== null ? (
                  <span className="cell-sub">Class pillar target: {selectedTargetXp} XP</span>
                ) : null}
              </span>
            </label>

            {pillarSelected ? (
              <div className="class-content-children">
                {children?.loading ? <p className="muted">Loading content…</p> : null}
                {children?.error ? (
                  <p className="form-error" role="alert">
                    {children.error}
                  </p>
                ) : null}
                {children?.loaded && !children.error ? (
                  <>
                    <div className="class-content-group">
                      <h4>Missions</h4>
                      {children.missions.length === 0 ? (
                        <p className="muted">No active missions.</p>
                      ) : (
                        children.missions.map((mission) => (
                          <label key={mission.id} className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={value.missionIds.includes(mission.id)}
                              disabled={disabled}
                              onChange={() => toggleId('missionIds', mission.id)}
                            />
                            <span>
                              {mission.name}{' '}
                              <span className="cell-sub">+{mission.xp} XP</span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                    <div className="class-content-group">
                      <h4>Learning lectures</h4>
                      {children.lectures.length === 0 ? (
                        <p className="muted">No active lectures.</p>
                      ) : (
                        children.lectures.map((lecture) => (
                          <label key={lecture.id} className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={value.learningLectureIds.includes(lecture.id)}
                              disabled={disabled}
                              onChange={() => toggleId('learningLectureIds', lecture.id)}
                            />
                            <span>
                              {lecture.title}{' '}
                              <span className="cell-sub">+{lecture.xp} XP</span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                    <div className="class-content-group">
                      <h4>Home activities</h4>
                      {children.homeActivities.length === 0 ? (
                        <p className="muted">No active home activities.</p>
                      ) : (
                        children.homeActivities.map((activity) => (
                          <label key={activity.id} className="checkbox-row">
                            <input
                              type="checkbox"
                              checked={value.homeActivityIds.includes(activity.id)}
                              disabled={disabled}
                              onChange={() => toggleId('homeActivityIds', activity.id)}
                            />
                            <span>
                              {activity.title}{' '}
                              <span className="cell-sub">+{activity.xp} XP</span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      {value.characterPillarIds.length > 0 ? (
        <p className="muted class-content-summary">
          {value.characterPillarIds.length} pillar
          {value.characterPillarIds.length === 1 ? '' : 's'}, {value.missionIds.length} mission
          {value.missionIds.length === 1 ? '' : 's'}, {value.learningLectureIds.length} lecture
          {value.learningLectureIds.length === 1 ? '' : 's'}, {value.homeActivityIds.length}{' '}
          home activit{value.homeActivityIds.length === 1 ? 'y' : 'ies'}
        </p>
      ) : null}
    </div>
  );
}
